import { FileBasic } from '@commenda/types/src/file/types';
import { ServiceRequestDetails } from '@commenda/types/src/service-request/types';
import {
  BusinessEntity,
  SalesTaxFilingFrequencyEnum,
  SalesTaxFilingStatusEnum,
  User,
} from '@prisma/client';

export interface SalesTaxFilingApiData {
  id: string; // Example: "fil_12345"
  corporation_id: string; // Example: "5ce0af37-2161-49c0-80c2-7b743f9cc869"
  registration_id: string; // Example: "reg_67890"
  form_title: string; // Example: "Sales and Use Tax Return"
  country: string; // Example: "US"
  state: string; // Example: "CA"
  filing_due_date: string; // Example: "2024-02-20"
  filing_frequency: SalesTaxFilingFrequencyEnum; // Example: "MONTHLY"
  tax_period_start_date: string; // Example: "2024-01-01"
  tax_period_end_date: string; // Example: "2024-01-31"
  reporting_period_start_date: string; // Example: "2024-01-01"
  reporting_period_end_date: string; // Example: "2024-01-31"
  status: SalesTaxFilingStatusEnum; // Example: "FILED"
  estimated_taxable_amount: number; // Example: 100000
  estimated_non_taxable_amount: number; // Example: 20000
  estimated_tax_collected: number; // Example: 8250
  filed_at?: string; // Example: "2024-02-15T14:30:00Z"
  taxable_amount: number; // Example: 98500
  non_taxable_amount: number; // Example: 19500
  tax_paid: number; // Example: 8127.50
  tax_liability: number;
  discount: number; // Example: 100
  rounding: number; // Example: 0.50
  penalty: number; // Example: 0
  interest: number; // Example: 0
  tax_remitted: number; // Example: 8028
  adjustment?: number; // Example: 12
  prepayment?: number; // Example: 12
  prior_payment?: number; // Example: -12
  retail_delivery_fee?: number; // Example: 12
  jurisdiction_issued_credit?: number; // Example: -12
  marketplace_payment?: number; // Example: -12.34
  vendor_discount?: number; // Example: -12
  filing_transactions_count?: number; // Example: 12
  filing_confirmation_number?: string; // Example: "FCN123456789"
  payment_confirmation_number?: string; // Example: "PCN987654321"
  created_at: number; // Example: 1710937200000 (Unix timestamp in milliseconds)
  filing_locked_at?: string | null;
}

export interface SalesTaxFilingListResponse {
  data: {
    filings: SalesTaxFilingApiData[]; // Array of Filing objects
    total_filings: number; // Example: 150
    cursor?: string; // Example: "aW50ZXJuYWxJZD0xMCxjcmVhdGVkQXQ9MTc0NDYxODQ2NzE0MTUyNA"
  };
  message: string;
}

export interface SalesTaxFiling {
  id: string;
  filing_id: string;
  sales_tax_corporation_id: string;
  form_title: string;
  country: string;
  state: string;
  filing_due_date: string;
  filing_frequency: SalesTaxFilingFrequencyEnum;
  tax_period_start_date: string;
  tax_period_end_date: string;
  reporting_period_start_date: string;
  reporting_period_end_date: string;
  status: SalesTaxFilingStatusEnum;
  sales_tax_created_at: Date;
  created_at: Date;
  last_updated_at: Date;
  ServiceRequest?: Partial<ServiceRequestDetails>;
  Corporation?: Partial<BusinessEntity>;
  assignedUser?: User;
  filedByAgent?: User;
  filed_at: string | null;
  filing_locked_at?: string | Date | null;

  estimated_taxable_amount: number;
  estimated_non_taxable_amount: number;
  estimated_marketplace_amount: number;
  estimated_exempt_amount: number;
  estimated_gross_amount: number;
  estimated_tax_collected: number;
  taxable_amount: number | null;
  non_taxable_amount: number | null;
  exempt_amount: number;
  gross_amount: number;
  marketplace_amount: number;
  tax_paid: number | null;
  tax_liability: number | null;
  tax_remitted: number | null;

  prepayment: number | null;
  prior_payment: number | null;
  retail_delivery_fee: number | null;
  jurisdiction_issued_credit: number | null;
  marketplace_payment: number | null;
  vendor_discount: number | null;
  rounding: number | null;
  penalty: number | null;
  interest: number | null;
  filing_transactions_count: number;

  filing_confirmation_number: string | null;
  payment_confirmation_number: string | null;

  discount: number | null;
}

export interface ListFilingsParams {
  corporationId: number;
  cursor?: string;
  limit?: number;
  status?: SalesTaxFilingStatusEnum[];
  frequency?: SalesTaxFilingFrequencyEnum[];
  jurisdiction?: string[];
  includeServiceRequest?: boolean;
  includeAgent?: boolean;
  includeAssignee?: boolean;
  taxPeriodEndDate?: string;
}

export interface UpdateFilingParams {
  taxable_amount?: number;
  non_taxable_amount?: number;
  tax_paid?: number;
  tax_liability?: number;
  discount?: number;
  rounding?: number;
  penalty?: number;
  interest?: number;
  tax_remitted?: number;
  adjustment?: number;
  prepayment?: number;
  prior_payment?: number;
  retail_delivery_fee?: number;
  jurisdiction_issued_credit?: number;
  marketplace_payment?: number;
  vendor_discount?: number;
  filing_confirmation_number?: string;
  payment_confirmation_number?: string;
  filed_at?: string;
}

export interface AssignmentResponse {
  success: boolean;
  error?: string;
  data?: SalesTaxFiling;
}

export enum FilingDocumentTypeEnum {
  RETURN = 'RETURN',
  FILING_CONFIRMATION = 'FILING_CONFIRMATION',
  PAYMENT_CONFIRMATION = 'PAYMENT_CONFIRMATION',
}

export interface FilingActionResponse {
  success: boolean;
  data?: SalesTaxFiling;
  error?: string;
}

export interface CreateFilingDocumentData {
  file: FileBasic;
  document_type?: FilingDocumentTypeEnum;
}

export interface FilingDocument {
  id: string; // Example: "fdoc_12345"
  filing_id: string; // Example: "fil_12345"
  file_id: string; // Example: "9864f66b-0c5f-4870-b146-e6e5c2a1a35e"
  type: FilingDocumentTypeEnum; // Example: "RETURN"
  created_at: number; // Example: 1710937200000 (Unix timestamp in milliseconds)
}

export interface JurisdictionBreakdown {
  jurisdiction_name: string;
  jurisdiction_type: string;
  tax_amount: number;
  rate: number;
  invoice_count: number;
  taxable_amount: number;
  non_taxable_amount: number;
}

export interface FilingTaxBreakdownSummary {
  filing_id: string;
  form_title: string;
  filing_due_date: string;
  tax_period_start_date: string;
  tax_period_end_date: string;
  status: string;
  state: string;
  total_taxable_amount: number;
  total_non_taxable_amount: number;
  total_tax_amount: number;
  invoice_count: number;
  jurisdiction_breakdown: JurisdictionBreakdown[];
}

export interface FilingTransactionSummaryResponse {
  data: FilingTaxBreakdownSummary[];
  message: string;
}

export interface FilingCalendarEntry {
  id: string;
  form_title: string;
  country: string;
  state: string;
  filing_due_date: string;
  tax_period_start_date: string;
  tax_period_end_date: string;
  reporting_period_start_date: string;
  reporting_period_end_date: string;
  status: SalesTaxFilingStatusEnum;
  filing_frequency: SalesTaxFilingFrequencyEnum;
}

export interface FilingFrequencyChangeLogSummary {
  corporation_id: string;
  state: string;
  country: string;
  previous_frequency: SalesTaxFilingFrequencyEnum;
  new_frequency: SalesTaxFilingFrequencyEnum;
  changed_at: number; // Unix timestamp in milliseconds
  created_at: number; // Unix timestamp in milliseconds
  internal_id?: number; // Optional since it has json:"-" tag
  added_entries: FilingCalendarEntry[];
  removed_entries: FilingCalendarEntry[];
  final_entries: FilingCalendarEntry[];
}

export interface GetFilingFrequencyChangeLogsByCorporationAndStateResponse {
  data: {
    changes: FilingFrequencyChangeLogSummary[];
    total_changes: number;
    cursor?: string;
  };
  message: string;
}

export interface FilingPrepaymentResponse {
  data: SalesTaxFiling[];
  message: string;
}

export enum BulkExportDeliveryType {
  EMAIL = 'EMAIL',
}

export interface ExportTaxBreakdownsRequest {
  filing_id: string;
  corporation_id: number;
  delivery_type: BulkExportDeliveryType;
  delivery_recipients: string[];
}

export interface ExportTaxBreakdownForFilingsWithinDateRangeRequest {
  due_date_start: string;
  due_date_end: string;
  corporation_id: number;
  delivery_type: BulkExportDeliveryType;
  delivery_recipients: string[];
}

export interface ExportTaxBreakdownsResponse {
  data: {
    job_id: string;
  };
  message: string;
}
